import React, { useState, useEffect } from 'react'
import { 
  Save,
  RotateCcw,
  Activity,
  Calendar,
  Target,
  Building,
  DollarSign,
  Plus,
  ChevronRight,
  Home,
  Users,
  MapPin,
  Phone,
  Mail,
  Globe,
  Tag,
  UserCheck,
  Video,
  Clock,
  CheckCircle,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import autoIncrementService from '../../services/autoIncrementService'
import { leadTrackingService } from '../../services/leadTrackingService'
import { leadGenerationService } from '../../services/leadGenerationService'

const LeadTrackingForm = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('trackingInfo')
  const [showHelp, setShowHelp] = useState(false)
  const [isLoadingTrackingCode, setIsLoadingTrackingCode] = useState(false)
  
  const [formData, setFormData] = useState({
    // Tracking Information
    trackingCode: '',
    trackingDate: new Date().toISOString().split('T')[0],
    trackingType: 'Status Update',
    trackingStatus: 'Completed',
    lead: '',
    // Status and Stage Information
    previousStatus: '',
    newStatus: '',
    previousStage: '',
    newStage: '',
    // Tracking Details
    trackingNotes: '',
    nextAction: '',
    nextActionDate: '',
    probability: 0,
    estimatedValue: 0,
    actualValue: 0,
    // Contact and Communication
    contactPerson: '',
    contactMethod: '',
    contactDuration: '',
    contactOutcome: '',
    // Follow-up Information
    followUpRequired: false,
    followUpDate: '',
    followUpNotes: '',
    assignedTo: ''
  })

  // Master data for dropdowns
  const [leads, setLeads] = useState([])
  const [users, setUsers] = useState([]) // Assuming a user service will be available

  // Load master data and tracking code on component mount
  useEffect(() => {
    loadTrackingCode()
    loadMasterData()
  }, [])

  const loadTrackingCode = async () => {
    try {
      setIsLoadingTrackingCode(true)
      const response = await autoIncrementService.getCurrentValue('LEAD-TRACK')
      if (response.success) {
        const sequence = response.data.formattedValue
        setFormData(prev => ({
          ...prev,
          trackingCode: sequence
        }))
      } else {
        // If sequence doesn't exist, show a placeholder
        setFormData(prev => ({
          ...prev,
          trackingCode: 'LT-0001' // Default placeholder
        }))
        console.warn('Auto-increment sequence LEAD-TRACK not found, using placeholder')
      }
    } catch (error) {
      console.error('Error loading tracking code:', error)
      // Set a placeholder code instead of showing error
      setFormData(prev => ({
        ...prev,
        trackingCode: 'LT-0001' // Default placeholder
      }))
      console.warn('Using placeholder tracking code due to auto-increment error')
    } finally {
      setIsLoadingTrackingCode(false)
    }
  }

  const loadMasterData = async () => {
    try {
      // Load leads
      const leadsResponse = await leadGenerationService.getAllLeads({ limit: 1000 })
      if (leadsResponse.success) {
        setLeads(leadsResponse.data.leads || [])
      } else {
        setLeads([])
        console.warn('Failed to load leads:', leadsResponse.message)
      }

      // Load users (you might need to create a user service)
      // const usersResponse = await userService.getAllUsers()
      // if (usersResponse.success) {
      //   setUsers(usersResponse.data.users)
      // }
    } catch (error) {
      console.error('Error loading master data:', error)
      setLeads([]) // Set empty array on error
      toast.error('Failed to load master data')
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      const trackingData = {
        ...formData
      }

      const response = await leadTrackingService.createLeadTracking(trackingData)
      if (response.success) {
        toast.success('Lead tracking record created successfully')
        navigate('/crm/lead-tracking')
      } else {
        toast.error(response.message || 'Failed to create lead tracking record')
      }
    } catch (error) {
      console.error('Error creating lead tracking record:', error)
      toast.error('Failed to create lead tracking record')
    }
  }

  const handleReset = () => {
    setFormData({
      trackingCode: '',
      trackingDate: new Date().toISOString().split('T')[0],
      trackingType: 'Status Update',
      trackingStatus: 'Completed',
      lead: '',
      previousStatus: '',
      newStatus: '',
      previousStage: '',
      newStage: '',
      trackingNotes: '',
      nextAction: '',
      nextActionDate: '',
      probability: 0,
      estimatedValue: 0,
      actualValue: 0,
      contactPerson: '',
      contactMethod: '',
      contactDuration: '',
      contactOutcome: '',
      followUpRequired: false,
      followUpDate: '',
      followUpNotes: '',
      assignedTo: ''
    })
    loadTrackingCode() // Reload auto-increment code
  }

  const helpContent = (
    <>
      <h3>What is Lead Tracking?</h3>
      <p>Lead Tracking lets you <strong>create and manage lead tracking records</strong>—status and stage updates, contact outcomes, next actions, and follow-up for a lead.</p>
      <h3>Tabs</h3>
      <p><strong>Tracking Info</strong> – Tracking code, date, type, status, lead, previous/new status and stage, notes, next action and date, probability, and value fields.</p>
      <p><strong>Contact &amp; Follow-up</strong> – Contact person, method, duration, outcome, follow-up required/date/notes, assigned to.</p>
      <h3>How to use</h3>
      <p>Select a lead and fill tracking details. Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/dashboard' },
          { label: 'CRM', path: '/crm' },
          { label: 'Lead Tracking', path: '/crm/lead-tracking' },
          { label: 'Create', isCurrent: true }
        ]}
        rightSlot={
          <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
            <HelpCircle size={18} />
            <span className="d-none d-sm-inline">Help</span>
          </button>
        }
      />
      
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title">
            <h1>Lead Tracking</h1>
            <p>Create and manage lead tracking record</p>
          </div>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Lead Tracking ( Create )</h2>
        </div>
      
      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'trackingInfo' ? 'active' : ''}`}
          onClick={() => setActiveTab('trackingInfo')}
        >
          <Activity size={18} />
          <span>Tracking Info</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'statusStage' ? 'active' : ''}`}
          onClick={() => setActiveTab('statusStage')}
        >
          <Target size={18} />
          <span>Status & Stage</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          <Phone size={18} />
          <span>Contact</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'followUp' ? 'active' : ''}`}
          onClick={() => setActiveTab('followUp')}
        >
          <Calendar size={18} />
          <span>Follow-up</span>
        </button>
      </div>
        
      <form className="form-content" onSubmit={handleSubmit}>

        {/* Tracking Information Tab */}
        {activeTab === 'trackingInfo' && (
          <div className="tab-content">
            <div className="section-header">
              <h3>Tracking Information</h3>
            </div>
            
            {/* Row 1: Tracking Code, Date, Type, Status */}
            <div className="grid-row-4">
              <div className="form-group">
                <label htmlFor="trackingCode" className="form-label required">
                  Tracking Code
                </label>
                <input
                  type="text"
                  id="trackingCode"
                  name="trackingCode"
                  value={isLoadingTrackingCode ? 'Generating...' : formData.trackingCode}
                  readOnly
                  className="form-input"
                  placeholder="Enter tracking code"
                  style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="trackingDate" className="form-label required">
                  Tracking Date
                </label>
                <input
                  type="date"
                  id="trackingDate"
                  name="trackingDate"
                  value={formData.trackingDate}
                  onChange={(e) => handleInputChange('trackingDate', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="trackingType" className="form-label required">
                  Tracking Type
                </label>
                <select
                  id="trackingType"
                  name="trackingType"
                  value={formData.trackingType}
                  onChange={(e) => handleInputChange('trackingType', e.target.value)}
                  className="form-input"
                >
                  <option value="Status Update">Status Update</option>
                  <option value="Stage Progression">Stage Progression</option>
                  <option value="Follow-up">Follow-up</option>
                  <option value="Meeting Scheduled">Meeting Scheduled</option>
                  <option value="Demo Completed">Demo Completed</option>
                  <option value="Proposal Sent">Proposal Sent</option>
                  <option value="Negotiation Started">Negotiation Started</option>
                  <option value="Deal Closed">Deal Closed</option>
                  <option value="Deal Lost">Deal Lost</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="trackingStatus" className="form-label required">
                  Tracking Status
                </label>
                <select
                  id="trackingStatus"
                  name="trackingStatus"
                  value={formData.trackingStatus}
                  onChange={(e) => handleInputChange('trackingStatus', e.target.value)}
                  className="form-input"
                >
                  <option value="Completed">Completed</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Pending">Pending</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Row 2: Lead Selection */}
            <div className="grid-row-1">
              <div className="form-group">
                <label htmlFor="lead" className="form-label required">
                  Select Lead
                </label>
                <select
                  id="lead"
                  name="lead"
                  value={formData.lead}
                  onChange={(e) => handleInputChange('lead', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Lead</option>
                  {leads && leads.length > 0 ? leads.map(lead => (
                    <option key={lead._id} value={lead._id}>
                      {lead.leadCode} - {lead.prospect?.prospectName || 'N/A'}
                    </option>
                  )) : (
                    <option value="" disabled>No leads available</option>
                  )}
                </select>
              </div>
            </div>

            {/* Row 3: Tracking Notes */}
            <div className="grid-row-1">
              <div className="form-group">
                <label htmlFor="trackingNotes" className="form-label required">
                  Tracking Notes
                </label>
                <textarea
                  id="trackingNotes"
                  name="trackingNotes"
                  value={formData.trackingNotes}
                  onChange={(e) => handleInputChange('trackingNotes', e.target.value)}
                  className="form-input"
                  rows="4"
                  placeholder="Enter detailed tracking notes"
                />
              </div>
            </div>
          </div>
        )}

        {/* Status & Stage Tab */}
        {activeTab === 'statusStage' && (
          <div className="tab-content">
            <div className="section-header">
              <h3>Status & Stage Information</h3>
            </div>
            
            {/* Row 1: Previous Status, New Status, Previous Stage, New Stage */}
            <div className="grid-row-4">
              <div className="form-group">
                <label htmlFor="previousStatus" className="form-label">
                  Previous Status
                </label>
                <select
                  id="previousStatus"
                  name="previousStatus"
                  value={formData.previousStatus}
                  onChange={(e) => handleInputChange('previousStatus', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Previous Status</option>
                  <option value="New">New</option>
                  <option value="Qualified">Qualified</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Demo Scheduled">Demo Scheduled</option>
                  <option value="Proposal Sent">Proposal Sent</option>
                  <option value="Negotiation">Negotiation</option>
                  <option value="Converted">Converted</option>
                  <option value="Lost">Lost</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="newStatus" className="form-label required">
                  New Status
                </label>
                <select
                  id="newStatus"
                  name="newStatus"
                  value={formData.newStatus}
                  onChange={(e) => handleInputChange('newStatus', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select New Status</option>
                  <option value="New">New</option>
                  <option value="Qualified">Qualified</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Demo Scheduled">Demo Scheduled</option>
                  <option value="Proposal Sent">Proposal Sent</option>
                  <option value="Negotiation">Negotiation</option>
                  <option value="Converted">Converted</option>
                  <option value="Lost">Lost</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="previousStage" className="form-label">
                  Previous Stage
                </label>
                <select
                  id="previousStage"
                  name="previousStage"
                  value={formData.previousStage}
                  onChange={(e) => handleInputChange('previousStage', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Previous Stage</option>
                  <option value="Lead">Lead</option>
                  <option value="Qualified">Qualified</option>
                  <option value="Proposal">Proposal</option>
                  <option value="Negotiation">Negotiation</option>
                  <option value="Closed Won">Closed Won</option>
                  <option value="Closed Lost">Closed Lost</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="newStage" className="form-label">
                  New Stage
                </label>
                <select
                  id="newStage"
                  name="newStage"
                  value={formData.newStage}
                  onChange={(e) => handleInputChange('newStage', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select New Stage</option>
                  <option value="Lead">Lead</option>
                  <option value="Qualified">Qualified</option>
                  <option value="Proposal">Proposal</option>
                  <option value="Negotiation">Negotiation</option>
                  <option value="Closed Won">Closed Won</option>
                  <option value="Closed Lost">Closed Lost</option>
                </select>
              </div>
            </div>

            {/* Row 2: Probability, Estimated Value, Actual Value */}
            <div className="grid-row-3">
              <div className="form-group">
                <label htmlFor="probability" className="form-label">
                  Probability (%)
                </label>
                <input
                  type="number"
                  id="probability"
                  name="probability"
                  value={formData.probability}
                  onChange={(e) => handleInputChange('probability', parseInt(e.target.value) || 0)}
                  className="form-input"
                  placeholder="0-100"
                  min="0"
                  max="100"
                />
              </div>

              <div className="form-group">
                <label htmlFor="estimatedValue" className="form-label">
                  Estimated Value
                </label>
                <input
                  type="number"
                  id="estimatedValue"
                  name="estimatedValue"
                  value={formData.estimatedValue}
                  onChange={(e) => handleInputChange('estimatedValue', parseFloat(e.target.value) || 0)}
                  className="form-input"
                  placeholder="Enter estimated value"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label htmlFor="actualValue" className="form-label">
                  Actual Value
                </label>
                <input
                  type="number"
                  id="actualValue"
                  name="actualValue"
                  value={formData.actualValue}
                  onChange={(e) => handleInputChange('actualValue', parseFloat(e.target.value) || 0)}
                  className="form-input"
                  placeholder="Enter actual value"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            {/* Row 3: Next Action, Next Action Date */}
            <div className="grid-row-2">
              <div className="form-group">
                <label htmlFor="nextAction" className="form-label">
                  Next Action
                </label>
                <input
                  type="text"
                  id="nextAction"
                  name="nextAction"
                  value={formData.nextAction}
                  onChange={(e) => handleInputChange('nextAction', e.target.value)}
                  className="form-input"
                  placeholder="Enter next action"
                />
              </div>

              <div className="form-group">
                <label htmlFor="nextActionDate" className="form-label">
                  Next Action Date
                </label>
                <input
                  type="date"
                  id="nextActionDate"
                  name="nextActionDate"
                  value={formData.nextActionDate}
                  onChange={(e) => handleInputChange('nextActionDate', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <div className="tab-content">
            <div className="section-header">
              <h3>Contact Information</h3>
            </div>
            
            {/* Row 1: Contact Person, Contact Method, Contact Duration, Contact Outcome */}
            <div className="grid-row-4">
              <div className="form-group">
                <label htmlFor="contactPerson" className="form-label">
                  Contact Person
                </label>
                <input
                  type="text"
                  id="contactPerson"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                  className="form-input"
                  placeholder="Enter contact person name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactMethod" className="form-label">
                  Contact Method
                </label>
                <select
                  id="contactMethod"
                  name="contactMethod"
                  value={formData.contactMethod}
                  onChange={(e) => handleInputChange('contactMethod', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Contact Method</option>
                  <option value="Phone">Phone</option>
                  <option value="Email">Email</option>
                  <option value="Meeting">Meeting</option>
                  <option value="Demo">Demo</option>
                  <option value="Video Call">Video Call</option>
                  <option value="In-Person">In-Person</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="contactDuration" className="form-label">
                  Contact Duration (minutes)
                </label>
                <input
                  type="number"
                  id="contactDuration"
                  name="contactDuration"
                  value={formData.contactDuration}
                  onChange={(e) => handleInputChange('contactDuration', parseInt(e.target.value) || 0)}
                  className="form-input"
                  placeholder="Enter duration in minutes"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactOutcome" className="form-label">
                  Contact Outcome
                </label>
                <select
                  id="contactOutcome"
                  name="contactOutcome"
                  value={formData.contactOutcome}
                  onChange={(e) => handleInputChange('contactOutcome', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Contact Outcome</option>
                  <option value="Positive">Positive</option>
                  <option value="Neutral">Neutral</option>
                  <option value="Negative">Negative</option>
                  <option value="No Response">No Response</option>
                  <option value="Rescheduled">Rescheduled</option>
                </select>
              </div>
            </div>

            {/* Row 2: Assigned To */}
            <div className="grid-row-1">
              <div className="form-group">
                <label htmlFor="assignedTo" className="form-label">
                  Assigned To
                </label>
                <input
                  type="text" // This should ideally be a select from users
                  id="assignedTo"
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                  className="form-input"
                  placeholder="Enter assigned person"
                />
              </div>
            </div>
          </div>
        )}

        {/* Follow-up Tab */}
        {activeTab === 'followUp' && (
          <div className="tab-content">
            <div className="section-header">
              <h3>Follow-up Information</h3>
            </div>
            
            {/* Row 1: Follow-up Required, Follow-up Date */}
            <div className="grid-row-2">
              <div className="form-group">
                <label htmlFor="followUpRequired" className="form-label">
                  Follow-up Required
                </label>
                <select
                  id="followUpRequired"
                  name="followUpRequired"
                  value={formData.followUpRequired}
                  onChange={(e) => handleInputChange('followUpRequired', e.target.value === 'true')}
                  className="form-input"
                >
                  <option value={false}>No</option>
                  <option value={true}>Yes</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="followUpDate" className="form-label">
                  Follow-up Date
                </label>
                <input
                  type="date"
                  id="followUpDate"
                  name="followUpDate"
                  value={formData.followUpDate}
                  onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            {/* Row 2: Follow-up Notes */}
            <div className="grid-row-1">
              <div className="form-group">
                <label htmlFor="followUpNotes" className="form-label">
                  Follow-up Notes
                </label>
                <textarea
                  id="followUpNotes"
                  name="followUpNotes"
                  value={formData.followUpNotes}
                  onChange={(e) => handleInputChange('followUpNotes', e.target.value)}
                  className="form-input"
                  rows="4"
                  placeholder="Enter follow-up notes"
                />
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleReset}
          >
            <RotateCcw size={18} />
            Reset
          </button>
          <button
            type="submit"
            className="btn-primary"
          >
            <Save size={18} />
            Save
          </button>
        </div>
      </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Lead Tracking – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default LeadTrackingForm
